import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { toast } from "sonner";
import WeatherDisplay from './WeatherDisplay';
import ForecastSelector from './ForecastSelector';
import AutocompleteInput from './AutocompleteInput';
import SafeComponent from './SafeComponent';
import { getLayerRecommendations } from '../lib/layerRecommendationsApi';
import { getPersonalizedWeatherRecommendations } from '../lib/personalizedWeatherApi';
import { reverseGeocode } from '../lib/reverseGeocode';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useSafeState } from '../hooks/useSafeState';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
const Weather = ({
  theme,
  onRecommendationShown,
  activityMode = 'walk',
  isPersonalized = false,
  selectedDate = null
}) => {
  const [location, setLocation] = useSafeState('');
  const [coordinates, setCoordinates] = useSafeState(null);
  const [inputError, setInputError] = useSafeState('');
  const [forecastHours, setForecastHours] = useSafeState(0);
  const { preferences } = useUserPreferences();
  const {
    handleError
  } = useErrorHandler();
  useEffect(() => {
    console.log('Weather component mounted, checking geolocation...');
    if ("geolocation" in navigator) {
      console.log('Geolocation is supported, requesting position...');
      navigator.geolocation.getCurrentPosition(async position => {
        console.log('Geolocation success:', position.coords);
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        setCoordinates(coords);

        // Try to get actual location name
        try {
          const locationName = await reverseGeocode(coords.latitude, coords.longitude);
          console.log('Reverse geocoding result:', locationName);
          if (locationName) {
            setLocation(locationName);
            console.log('Location set to:', locationName);
          } else {
            setLocation(`${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`);
            console.log('Using coordinates as fallback location');
          }
        } catch (error) {
          console.error('Failed to get location name:', error);
          setLocation(`${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`);
        }
      }, error => {
        console.error("Geolocation error:", error.code, error.message);
        let errorMessage;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enter a location manually.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable. Please enter a location manually.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please enter a location manually.';
            break;
          default:
            errorMessage = 'Unable to retrieve your location. Please enter a location manually.';
            break;
        }
        setInputError(errorMessage);
      }, {
        timeout: 10000,
        enableHighAccuracy: false,
        maximumAge: 300000
      });
    } else {
      console.log('Geolocation not supported');
      setInputError('Geolocation is not supported by your browser. Please enter a location manually.');
    }
  }, []);
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: [
      isPersonalized ? 'personalizedRecommendations' : 'layerRecommendations',
      coordinates,
      forecastHours,
      activityMode,
      selectedDate?.toISOString(),
      preferences?.layer_shift,
      preferences?.vote_shift
    ],
    queryFn: () => {
      if (!coordinates) return null;
      
      if (isPersonalized) {
        return getPersonalizedWeatherRecommendations({
          ...coordinates,
          forecastHours,
          activityMode,
          userPreferences: preferences || {},
          selectedDate
        });
      } else {
        return getLayerRecommendations({
          ...coordinates,
          forecastHours
        });
      }
    },
    enabled: !!coordinates,
    retry: (failureCount, error) => {
      // Don't retry on rate limit errors
      if (error?.status === 429) return false;
      return failureCount < 3;
    },
    onError: error => {
      if (error?.status === 429) {
        const retryAfter = error.response?.headers?.get('Retry-After') || 30;
        toast.error(`Rate limit exceeded. Please try again in ${retryAfter} seconds.`);
      } else {
        handleError(error, 'Weather Data');
      }
    }
  });
  const handleLocationSelect = selectedLocation => {
    setLocation(selectedLocation.name);
    setCoordinates({
      latitude: selectedLocation.latitude,
      longitude: selectedLocation.longitude
    });
    setInputError('');
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (coordinates) {
      refetch();
    } else {
      setInputError('Please select a location from the dropdown.');
    }
  };
  return <SafeComponent name="Weather">
      <div className="w-full max-w-md mx-auto rounded-2xl">
        <form onSubmit={handleSubmit} className="mb-4">
          <div className="space-y-2">
            <SafeComponent name="Location Input">
              <AutocompleteInput onLocationSelect={handleLocationSelect} theme={theme} />
            </SafeComponent>
            {inputError && <p className="text-destructive text-caption">{inputError}</p>}
            <div className="flex space-x-2">
              <Button type="submit" variant="outline" className="flex-grow">
                Find
              </Button>
              <SafeComponent name="Forecast Selector">
                <ForecastSelector forecastHours={forecastHours} setForecastHours={setForecastHours} />
              </SafeComponent>
            </div>
          </div>
        </form>
        {isLoading && <div className="text-body-sm text-muted-foreground">Loading...</div>}
        {error && error.status !== 429 && <div className="text-body-sm text-destructive">
            Error: {error.message}
          </div>}
        {data && <SafeComponent name="Weather Display">
            <WeatherDisplay 
              data={data} 
              location={location} 
              coordinates={coordinates} 
              forecastHours={forecastHours} 
              onRecommendationShown={onRecommendationShown}
              isPersonalized={isPersonalized}
              activityMode={activityMode}
            />
          </SafeComponent>}
      </div>
    </SafeComponent>;
};
export default Weather;